// Mock data for Upload Data Management page

export interface Staff {
  id: string;
  name: string;
  email: string;
  department: string;
  role: string;
  status: 'active' | 'inactive' | 'pending';
  employeeId: string;
  startDate: string;
  contractType: 'full-time' | 'part-time' | 'temporary';
  subjects: string[];
}

export interface Student {
  id: string;
  name: string;
  studentid: string;
  yeargroup: string;
  class: string;
  status: 'active' | 'inactive' | 'transferred';
  dateOfBirth: string;
  guardianContact: string;
  medicalNotes?: string;
}

export interface Subject {
  id: string;
  name: string;
  code: string;
  department: string;
  level: 'primary' | 'secondary' | 'tertiary';
  status: 'active' | 'inactive' | 'archived';
  credits: number;
  description: string;
  prerequisites: string[];
}

export interface Room {
  id: string;
  name: string;
  type: 'classroom' | 'laboratory' | 'workshop' | 'gymnasium' | 'hall' | 'library';
  capacity: number;
  building: string;
  equipment: string[];
  floor: number;
  accessibility: boolean;
}

export interface ClassGroup {
  id: string;
  name: string;
  yeargroup: string;
  size: number;
  formtutor: string;
  room: string;
  students: string[];
}

export interface TimePeriod {
  id: string;
  name: string;
  starttime: string;
  endtime: string;
  duration: number;
  type: 'lesson' | 'break' | 'lunch' | 'assembly' | 'form';
  dayofweek: string;
}

export const mockData = {
  staff: [
    {
      id: 'ST001',
      name: 'Sarah Johnson',
      email: 'sarah.johnson@school.edu',
      department: 'Mathematics',
      role: 'Head of Department',
      status: 'active' as const,
      employeeId: 'EMP001',
      startDate: '2020-09-01',
      contractType: 'full-time' as const,
      subjects: ['Mathematics', 'Statistics'],
    },
    {
      id: 'ST002',
      name: 'James Wilson',
      email: 'james.wilson@school.edu',
      department: 'English',
      role: 'Teacher',
      status: 'active' as const,
      employeeId: 'EMP002',
      startDate: '2021-01-15',
      contractType: 'part-time' as const,
      subjects: ['English Literature', 'Creative Writing'],
    },
    {
      id: 'ST003',
      name: 'Emily Chen',
      email: 'emily.chen@school.edu',
      department: 'Science',
      role: 'Teacher',
      status: 'active' as const,
      employeeId: 'EMP003',
      startDate: '2019-08-25',
      contractType: 'full-time' as const,
      subjects: ['Chemistry', 'Biology'],
    },
    {
      id: 'ST004',
      name: 'Michael Brown',
      email: 'michael.brown@school.edu',
      department: 'History',
      role: 'Teacher',
      status: 'pending' as const,
      employeeId: 'EMP004',
      startDate: '2024-09-01',
      contractType: 'full-time' as const,
      subjects: ['History', 'Geography'],
    },
    {
      id: 'ST005',
      name: 'Lisa Davis',
      email: 'lisa.davis@school.edu',
      department: 'Modern Languages',
      role: 'Teacher',
      status: 'active' as const,
      employeeId: 'EMP005',
      startDate: '2018-02-12',
      contractType: 'full-time' as const,
      subjects: ['French', 'Spanish'],
    },
  ] as Staff[],

  students: [
    {
      id: 'STU001',
      name: 'Alex Thompson',
      studentid: 'STU2024001',
      yeargroup: 'Year 10',
      class: '10A',
      status: 'active' as const,
      dateOfBirth: '2008-03-15',
      guardianContact: 'parent1@email.com',
    },
    {
      id: 'STU002',
      name: 'Sophie Williams',
      studentid: 'STU2024002',
      yeargroup: 'Year 10',
      class: '10A',
      status: 'active' as const,
      dateOfBirth: '2008-07-22',
      guardianContact: 'parent2@email.com',
    },
    {
      id: 'STU003',
      name: 'Daniel Garcia',
      studentid: 'STU2024003',
      yeargroup: 'Year 11',
      class: '11B',
      status: 'active' as const,
      dateOfBirth: '2007-11-08',
      guardianContact: 'parent3@email.com',
    },
    {
      id: 'STU004',
      name: 'Emma Martinez',
      studentid: 'STU2024004',
      yeargroup: 'Year 9',
      class: '9C',
      status: 'active' as const,
      dateOfBirth: '2009-01-30',
      guardianContact: 'parent4@email.com',
    },
    {
      id: 'STU005',
      name: 'Ryan Lee',
      studentid: 'STU2024005',
      yeargroup: 'Year 8',
      class: '8D',
      status: 'transferred' as const,
      dateOfBirth: '2010-05-18',
      guardianContact: 'parent5@email.com',
    },
  ] as Student[],

  subjects: [
    {
      id: 'SUB001',
      name: 'Mathematics',
      code: 'MATH',
      department: 'Mathematics',
      level: 'secondary' as const,
      status: 'active' as const,
      credits: 5,
      description: 'Core mathematics curriculum covering algebra, geometry, and statistics',
      prerequisites: [],
    },
    {
      id: 'SUB002',
      name: 'English Literature',
      code: 'ENG',
      department: 'English',
      level: 'secondary' as const,
      status: 'active' as const,
      credits: 4,
      description: 'Study of classic and contemporary literature',
      prerequisites: [],
    },
    {
      id: 'SUB003',
      name: 'Chemistry',
      code: 'CHEM',
      department: 'Science',
      level: 'secondary' as const,
      status: 'active' as const,
      credits: 5,
      description: 'Comprehensive chemistry covering organic, inorganic, and physical chemistry',
      prerequisites: ['General Science'],
    },
    {
      id: 'SUB004',
      name: 'History',
      code: 'HIST',
      department: 'Humanities',
      level: 'secondary' as const,
      status: 'active' as const,
      credits: 3,
      description: 'World history from ancient civilizations to modern times',
      prerequisites: [],
    },
    {
      id: 'SUB005',
      name: 'French',
      code: 'FREN',
      department: 'Modern Languages',
      level: 'secondary' as const,
      status: 'active' as const,
      credits: 3,
      description: 'French language and culture studies',
      prerequisites: [],
    },
  ] as Subject[],

  rooms: [
    {
      id: 'R001',
      name: 'Mathematics Room 1',
      type: 'classroom' as const,
      capacity: 30,
      building: 'Main Building',
      equipment: ['Interactive Whiteboard', 'Projector', 'Computer'],
      floor: 1,
      accessibility: true,
    },
    {
      id: 'R002',
      name: 'Science Laboratory 1',
      type: 'laboratory' as const,
      capacity: 24,
      building: 'Science Block',
      equipment: ['Fume Cupboard', 'Gas Taps', 'Laboratory Benches', 'Safety Equipment'],
      floor: 2,
      accessibility: true,
    },
    {
      id: 'R003',
      name: 'Computer Suite 1',
      type: 'classroom' as const,
      capacity: 28,
      building: 'Technology Block',
      equipment: ['30 Desktop Computers', 'Network Printer', 'Interactive Whiteboard'],
      floor: 1,
      accessibility: true,
    },
    {
      id: 'R004',
      name: 'Sports Hall',
      type: 'gymnasium' as const,
      capacity: 60,
      building: 'Sports Complex',
      equipment: ['Basketball Hoops', 'Volleyball Net', 'Gymnastics Equipment'],
      floor: 0,
      accessibility: true,
    },
    {
      id: 'R005',
      name: 'Assembly Hall',
      type: 'hall' as const,
      capacity: 200,
      building: 'Main Building',
      equipment: ['Stage', 'Sound System', 'Lighting', 'Projector'],
      floor: 0,
      accessibility: true,
    },
  ] as Room[],

  classes: [
    {
      id: 'CL001',
      name: '10A',
      yeargroup: 'Year 10',
      size: 28,
      formtutor: 'Sarah Johnson',
      room: 'R101',
      students: ['STU001', 'STU002'],
    },
    {
      id: 'CL002',
      name: '11B',
      yeargroup: 'Year 11',
      size: 26,
      formtutor: 'James Wilson',
      room: 'R201',
      students: ['STU003'],
    },
    {
      id: 'CL003',
      name: '9C',
      yeargroup: 'Year 9',
      size: 30,
      formtutor: 'Emily Chen',
      room: 'R301',
      students: ['STU004'],
    },
  ] as ClassGroup[],

  timePeriods: [
    {
      id: 'P001',
      name: 'Period 1',
      starttime: '08:45',
      endtime: '09:45',
      duration: 60,
      type: 'lesson' as const,
      dayofweek: 'Monday',
    },
    {
      id: 'P002',
      name: 'Period 2',
      starttime: '09:45',
      endtime: '10:45',
      duration: 60,
      type: 'lesson' as const,
      dayofweek: 'Monday',
    },
    {
      id: 'P003',
      name: 'Break',
      starttime: '10:45',
      endtime: '11:00',
      duration: 15,
      type: 'break' as const,
      dayofweek: 'Monday',
    },
    {
      id: 'P004',
      name: 'Period 3',
      starttime: '11:00',
      endtime: '12:00',
      duration: 60,
      type: 'lesson' as const,
      dayofweek: 'Monday',
    },
    {
      id: 'P005',
      name: 'Lunch',
      starttime: '12:00',
      endtime: '13:00',
      duration: 60,
      type: 'lunch' as const,
      dayofweek: 'Monday',
    },
  ] as TimePeriod[],
};
